import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MainLayoutWrapper from '@shared/layouts/MainLayoutWrapper';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@shared/contexts/ThemeContext';
import { ImpersonationProvider } from '@shared/contexts/ImpersonationContext';
import { DialogProvider } from '@shared/contexts/DialogContext';
import { ErrorBoundary } from '@shared/components/ErrorBoundary';
import CookieConsent from '@shared/components/CookieConsent';
import ScrollRestoration from '@shared/components/ScrollRestoration';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import RootRedirect from '@components/RootRedirect';
import Home from '@pages/Home';
import LandingPage from '@features/landing/pages/LandingPage';

// Auth feature pages
import Register from '@features/auth/pages/Register';
import Login from '@features/auth/pages/Login';
import VerifyEmail from '@features/auth/pages/VerifyEmail';
import ForgotPassword from '@features/auth/pages/ForgotPassword';
import ResetPassword from '@features/auth/pages/ResetPassword';
import MFASetup from '@features/auth/pages/MFASetup';
import AcceptInvitation from '@features/auth/pages/AcceptInvitation';

// Dashboard feature pages
import Dashboard from '@features/dashboard/pages/Dashboard';
import Metrics from '@features/dashboard/pages/Metrics';

// Settings feature pages
import Profile from '@features/settings/pages/Profile';
import DeleteAccount from '@features/settings/pages/DeleteAccount';
import ChangePassword from '@features/settings/pages/ChangePassword';
import TeamMembers from '@features/settings/pages/TeamMembers';
import Organizations from '@features/settings/pages/Organizations';
import OrganizationDetail from '@features/settings/pages/OrganizationDetail';
import CustomRoles from '@features/settings/pages/CustomRoles';
import Settings from '@features/settings/pages/Settings';

// Backups feature pages
import BackupConfigs from '@features/backups/pages/BackupConfigs';
import BackupJobs from '@features/backups/pages/BackupJobs';
import Schedules from '@features/backups/pages/Schedules';
import ConfigSnapshots from '@features/backups/pages/ConfigSnapshots';
import QuickBackup from '@features/backups/pages/QuickBackup';
import BackupAlertStateDetail from '@features/backups/pages/BackupAlertStateDetail';

// Restores feature pages
import RestoreJobs from '@features/restores/pages/RestoreJobs';

// Agents feature pages
import Agents from '@features/agents/pages/Agents';
import AgentDetails from '@features/agents/pages/AgentDetails';
import AgentGroups from '@features/agents/pages/AgentGroups';
import HealthDetails from '@features/agents/pages/HealthDetails';

// Billing feature pages
import Billing from '@features/billing/pages/Billing';
import BillingSuccess from '@features/billing/pages/BillingSuccess';
import Checkout from '@features/billing/pages/Checkout';
import Invoices from '@features/billing/pages/Invoices';
import Pricing from '@features/billing/pages/Pricing';

// Storage feature pages
import StorageDestinations from '@features/storage/pages/StorageDestinations';
import StorageBreakdown from '@features/storage/pages/StorageBreakdown';
import DataLocations from '@features/storage/pages/DataLocations';

// Notifications feature pages
import Notifications from '@features/notifications/pages/Notifications';
import Integrations from '@features/notifications/pages/Integrations';

// Logs feature pages
import AuditLogs from '@features/logs/pages/AuditLogs';
import SystemLogs from '@features/logs/pages/SystemLogs';
import ApiUsage from '@features/logs/pages/ApiUsage';
import MetricsAlerts from '@features/logs/pages/MetricsAlerts';
import ApiKeys from '@features/logs/pages/ApiKeys';
import Webhooks from '@features/logs/pages/Webhooks';
import SMTPSettings from '@features/logs/pages/SMTPSettings';

// Compliance feature pages
import DataExport from '@features/compliance/pages/DataExport';
import DataProcessingAgreement from '@features/compliance/pages/DataProcessingAgreement';
import Consents from '@features/compliance/pages/Consents';
import ComplianceReports from '@features/compliance/pages/ComplianceReports';
import DataBreachesPage from '@features/compliance/pages/DataBreachesPage';
import LogRetentionSettings from '@features/compliance/pages/LogRetentionSettings';

// Admin feature pages
import AdminSettings from '@features/admin/pages/AdminSettings';
import AdminSetup from '@features/admin/pages/AdminSetup';
import PlatformAdminDashboard from '@features/admin/pages/PlatformAdminDashboard';
import PlatformAnalytics from '@features/admin/pages/PlatformAnalytics';
import PlatformReports from '@features/admin/pages/PlatformReports';
import SystemHealth from '@features/admin/pages/SystemHealth';
import CustomerManagement from '@features/admin/pages/CustomerManagement';
import CustomerDetails from '@features/admin/pages/CustomerDetails';
import BinaryManagement from '@features/admin/pages/BinaryManagement';
import PlanManagement from '@features/admin/pages/PlanManagement';

// Legal feature pages
import TermsOfService from '@features/legal/pages/TermsOfService';
import PrivacyPolicy from '@features/legal/pages/PrivacyPolicy';
import CookiePolicy from '@features/legal/pages/CookiePolicy';

// Shared pages
import NotFound from '@shared/pages/NotFound';
import ServerError from '@shared/pages/ServerError';

// Test pages
import LoggerTestPage from './test/LoggerTestPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (
          typeof error === 'object' &&
          error !== null &&
          'appError' in error &&
          (error as { appError?: { type?: string } }).appError?.type === 'AUTHENTICATION'
        ) {
          return false;
        }
        // Don't retry on validation errors
        if (
          typeof error === 'object' &&
          error !== null &&
          'appError' in error &&
          (error as { appError?: { type?: string } }).appError?.type === 'VALIDATION'
        ) {
          return false;
        }
        // Don't retry on authorization errors
        if (
          typeof error === 'object' &&
          error !== null &&
          'appError' in error &&
          (error as { appError?: { type?: string } }).appError?.type === 'AUTHORIZATION'
        ) {
          return false;
        }
        // Retry network/server errors up to 3 times
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {},
  },
});

// React Router v7 future flags
const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

function App() {
  return (
    <Router future={routerFuture}>
      <ScrollRestoration />
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ImpersonationProvider>
            <DialogProvider>
              <ErrorBoundary>
                <Routes>
                  {/* Public routes */}
                  <Route path="/landing" element={<LandingPage />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/admin/setup" element={<AdminSetup />} />
                  {/* Email confirmation links must remain reachable after logout. */}
                  <Route path="/delete-account" element={<DeleteAccount />} />

                  {/* Protected routes with MainLayout wrapper - uses nested routes with Outlet */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <MainLayoutWrapper />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/mfa/setup" element={<MFASetup />} />
                    <Route path="/backup-jobs" element={<BackupJobs />} />
                    <Route path="/restore-jobs" element={<RestoreJobs />} />
                    <Route path="/organizations" element={<Organizations />} />
                    <Route path="/organizations/:id" element={<OrganizationDetail />} />
                    <Route path="/team-members" element={<TeamMembers />} />
                    <Route path="/agents" element={<Agents />} />
                    <Route path="/agents/:id" element={<AgentDetails />} />
                    <Route path="/backups/configs" element={<BackupConfigs />} />
                    <Route path="/backups/configs/:id/edit" element={<BackupConfigs />} />
                    <Route path="/backups/configs/:id/snapshots" element={<ConfigSnapshots />} />
                    <Route
                      path="/backups/configs/:id/alert-state"
                      element={<BackupAlertStateDetail />}
                    />
                    <Route path="/backups/quick" element={<QuickBackup />} />
                    <Route path="/schedules" element={<Schedules />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/webhooks" element={<Webhooks />} />
                    <Route path="/metrics-alerts" element={<MetricsAlerts />} />
                    <Route path="/metrics" element={<Metrics />} />
                    <Route path="/smtp-settings" element={<SMTPSettings />} />
                    <Route path="/log-retention-settings" element={<LogRetentionSettings />} />
                    <Route path="/data-export" element={<DataExport />} />
                    <Route path="/dpa" element={<DataProcessingAgreement />} />
                    <Route path="/consents" element={<Consents />} />
                    <Route path="/data-locations" element={<DataLocations />} />
                    <Route path="/compliance-reports" element={<ComplianceReports />} />
                    <Route path="/agent-groups" element={<AgentGroups />} />
                    <Route path="/custom-roles" element={<CustomRoles />} />
                    <Route path="/api-keys" element={<ApiKeys />} />
                    <Route path="/api-usage" element={<ApiUsage />} />
                    <Route path="/admin/integrations" element={<Integrations />} />
                    <Route path="/admin/dashboard" element={<PlatformAdminDashboard />} />
                    <Route path="/admin/analytics" element={<PlatformAnalytics />} />
                    <Route path="/admin/health" element={<SystemHealth />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/reports" element={<PlatformReports />} />
                    <Route path="/admin/customers" element={<CustomerManagement />} />
                    <Route path="/admin/customers/:id" element={<CustomerDetails />} />
                    <Route path="/admin/binaries" element={<BinaryManagement />} />
                    <Route path="/admin/plans" element={<PlanManagement />} />
                    <Route path="/audit-logs" element={<AuditLogs />} />
                    <Route path="/data-breaches" element={<DataBreachesPage />} />
                    <Route path="/system-logs" element={<SystemLogs />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/storage-breakdown" element={<StorageBreakdown />} />
                    <Route path="/storage-destinations" element={<StorageDestinations />} />
                    <Route path="/health-details" element={<HealthDetails />} />
                  </Route>

                  {/* Server error route */}
                  <Route path="/500" element={<ServerError />} />

                  {/* Test routes (development only) */}
                  {import.meta.env.MODE === 'development' && (
                    <Route path="/test-logger" element={<LoggerTestPage />} />
                  )}

                  {/* 404 catch-all route */}
                  <Route path="*" element={<NotFound />} />

                  {/* Default route - checks if setup is needed (Feature 658) */}
                  <Route path="/" element={<RootRedirect />} />
                </Routes>
                <CookieConsent />
              </ErrorBoundary>
            </DialogProvider>
          </ImpersonationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
